import React, { useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import SearchDCInput from '../../search-dc/SearchDCInput';
import StatCard from './StatCard';
import ServiceReachabilityChart from './charts/ServiceReachabilityChart';
import LatencyDistributionChart from './charts/LatencyDistributionChart';
import PingDelayChart from './charts/PingDelayChart';
import { getDevicesDashboardDataInit, getDevicesPingStatusInit } from '../redux/actions';
import '../supplyChainStyle.scss';
import DevicePingTable from './DevicePingTable';
import { STAT_CARD_CONFIG } from '../constants/dashboardDefaults';

const getDateRange = (hours) => {
    const to = new Date();
    const from = new Date(to.getTime() - hours * 60 * 60 * 1000);
    return {
        from: from.toISOString(),
        to: to.toISOString()
    };
};

function Dashboard() {
    const dashboardData  = useSelector(state => state.supplyChain.dashboard.dashboardData);
    const loading        = useSelector(state => state.supplyChain.dashboard.loading);
    const pingLoading    = useSelector(state => state.supplyChain.pingStatus.loading);
    const error          = useSelector(state => state.supplyChain.dashboard.error);
    const devicesSummary = useSelector(state => state.supplyChain.pingStatus.summary);
    const pingDevices    = useSelector(state => state.supplyChain.pingStatus.devices);
    const selectedDCs    = useSelector(state => state.searchDC.selectedDCList);
    const dispatch = useDispatch();

    const [selectedHours, setSelectedHours] = useState(24);

    const stats = useMemo(() => ({
        totalDevices: devicesSummary?.totalDevices ?? null,
        reachable:    devicesSummary?.reachable    ?? null,
        unreachable:  devicesSummary?.notReachable ?? null,
        avgLatencyMs: devicesSummary?.avgLatencyMs ?? null
    }), [devicesSummary]);

    const isFetching = loading || pingLoading;

    const storeCodes = useMemo(
        () => selectedDCs?.map(dc => dc.code).join(',') ?? '',
        [selectedDCs]
    );

    const fetchDashboard = (hours) => {
        if (isFetching) return;
        if (storeCodes) {
            const { from, to } = getDateRange(hours);
            dispatch(getDevicesDashboardDataInit({ storeCodes, from, to, latencyThresholdMs: 90, limit: 10 }));
            dispatch(getDevicesPingStatusInit(storeCodes));
        }
    };

    useEffect(() => {
        fetchDashboard(selectedHours);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const searchMetricsByDC = () => fetchDashboard(selectedHours);

    const handleRangeChange = (hours) => {
        setSelectedHours(hours);
        if (storeCodes) {
            const { from, to } = getDateRange(hours);
            dispatch(getDevicesDashboardDataInit({ storeCodes, from, to, latencyThresholdMs: 90, limit: 10 }));
        }
    };

    const handlePingRefresh = () => {
        if (storeCodes) dispatch(getDevicesPingStatusInit(storeCodes));
    };

    const handleDashboardRefresh = () => {
        if (isFetching || !storeCodes) return;
        const { from, to } = getDateRange(selectedHours);
        dispatch(getDevicesDashboardDataInit({ storeCodes, from, to, latencyThresholdMs: 90, limit: 10 }));
    };

    return (
        <div className="supply-chain-container container-fluid">
            <p className="page-label">
                Supply Chain
                <i className="pi pi-angle-right" />
                Dashboard
            </p>

            <SearchDCInput
                handleClick={searchMetricsByDC}
                disabled={isFetching}
            />

            <div className="stat-cards-row">
                {STAT_CARD_CONFIG.map(({ key, title, subtitle, icon, colorClass }) => (
                    <StatCard
                        key={key}
                        title={title}
                        value={stats[key]}
                        subtitle={subtitle}
                        icon={icon}
                        colorClass={colorClass}
                    />
                ))}
            </div>

            <div className="charts-row">
                <ServiceReachabilityChart
                    deviceReachability={dashboardData?.deviceReachability}
                    loading={loading}
                    error={error}
                />
                <LatencyDistributionChart
                    deviceLatency={dashboardData?.deviceLatency}
                    loading={loading}
                    error={error}
                />
            </div>

            <PingDelayChart
                topNetworkPingDelay={dashboardData?.topNetworkPingDelay}
                loading={loading}
                error={error}
                selectedHours={selectedHours}
                onRangeChange={handleRangeChange}
                onRefresh={handleDashboardRefresh}
            />

            <DevicePingTable
                devices={pingDevices}
                onRefresh={handlePingRefresh}
                loading={pingLoading}
            />
        </div>
    );
}

export default Dashboard;